import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { PUBLIC_ERROR_MESSAGES } from "@nexra/contracts";

import en from "./locales/en.json";
import zhCN from "./locales/zh-CN.json";

export const locales = ["zh-CN", "en"] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = "zh-CN";
export const localeStorageKey = "nexra.locale";

export function isLocale(value: unknown): value is Locale {
  return value === "zh-CN" || value === "en";
}

function storedLocale(): Locale {
  if (typeof localStorage === "undefined") return defaultLocale;
  const stored = localStorage.getItem(localeStorageKey);
  return isLocale(stored) ? stored : defaultLocale;
}

function persistLocale(locale: string): void {
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = locale;
  }
  if (typeof localStorage !== "undefined") localStorage.setItem(localeStorageKey, locale);
}

void i18n.use(initReactI18next).init({
  resources: {
    "zh-CN": { translation: { ...zhCN, publicErrors: PUBLIC_ERROR_MESSAGES["zh-CN"] } },
    en: { translation: { ...en, publicErrors: PUBLIC_ERROR_MESSAGES.en } },
  },
  lng: storedLocale(),
  fallbackLng: defaultLocale,
  supportedLngs: [...locales],
  interpolation: { escapeValue: false },
  returnNull: false,
});

i18n.on("languageChanged", persistLocale);
persistLocale(i18n.language);

export function getLocale(): Locale {
  return isLocale(i18n.language) ? i18n.language : defaultLocale;
}

export function changeLocale(locale: Locale): Promise<unknown> {
  persistLocale(locale);
  return i18n.changeLanguage(locale);
}

export function translateKey(key: string, options?: Record<string, unknown>): string {
  return i18n.t(key as never, options);
}

export function hasTranslation(key: string): boolean {
  return i18n.exists(key as never);
}

export default i18n;
